import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import { Loader2, Printer, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";

const MAX_BULK_PRINT_ORDERS = 100;

export default function OrderPrintView() {
  const { orderId } = useParams();
  const location = useLocation();
  const [orders, setOrders] = useState(null);
  const [invoiceMarkup, setInvoiceMarkup] = useState([]);
  const [error, setError] = useState(null);
  const frameRefs = useRef({});
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const isPreview = searchParams.get("preview") === "1";
  const orderIds = useMemo(() => {
    const ids = orderId ? [orderId] : (searchParams.get("ids") || "").split(",");
    return [...new Set(ids.filter(Boolean))].slice(0, MAX_BULK_PRINT_ORDERS);
  }, [orderId, searchParams]);

  useEffect(() => {
    if (!orderIds.length) {
      setError("Choose at least one order to print.");
      return;
    }
    api.get("/admin/orders/print", { params: { ids: orderIds.join(",") } })
      .then(async (orderResponse) => {
        const printedOrders = isPreview
          ? orderResponse.data
          : (await api.post("/admin/orders/mark-printed", { ids: orderIds })).data;
        setOrders(printedOrders);
        const renderedInvoices = await Promise.all(
          printedOrders.map(async (order) => {
            const response = await api.get(`/admin/orders/${order.id}/invoice.html`, {
              params: { assign_number: !isPreview },
              responseType: "text",
            });
            return { id: order.id, html: response.data };
          }),
        );
        setInvoiceMarkup(renderedInvoices);
      })
      .catch((requestError) => {
        const message = formatError(requestError);
        setError(message);
        toast.error(message);
      });
  }, [isPreview, orderIds]);

  if (error) {
    return <div className="p-8 text-red-600" data-testid="print-error">Failed to load: {error}</div>;
  }
  if (!orders || invoiceMarkup.length !== orders.length) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  const printInvoices = () => {
    invoiceMarkup.forEach((invoice) => {
      const frameWindow = frameRefs.current[invoice.id]?.contentWindow;
      frameWindow?.focus();
      frameWindow?.print();
    });
  };

  return (
    <div className="min-h-screen bg-white text-[#1E2A24]" data-testid="print-invoice">
      <div className="no-print sticky top-0 z-10 flex items-center justify-between border-b bg-[#F4F1EA] px-6 py-3">
        <div className="text-sm text-[#64748B]" data-testid="print-preview-status">
          {isPreview
            ? `Previewing ${orders.length} invoice${orders.length === 1 ? "" : "s"}.`
            : `Printing ${orders.length} invoice${orders.length === 1 ? "" : "s"}.`}
        </div>
        <div className="flex items-center gap-2">
          {isPreview && (
            <button
              type="button"
              onClick={() => window.close()}
              className="flex items-center gap-2 rounded-lg border border-[#C8BFB0] px-4 py-2 text-sm font-semibold text-[#1E2A24]"
              data-testid="close-print-preview"
            >
              <X size={14} /> Close
            </button>
          )}
          <button
            type="button"
            onClick={printInvoices}
            className="flex items-center gap-2 rounded-lg bg-[#2D5A47] px-4 py-2 text-sm font-semibold text-white"
            data-testid="print-again"
          >
            <Printer size={14} /> Print master invoice
          </button>
        </div>
      </div>
      <div className="bg-[#F4F1EA] py-6">
        {invoiceMarkup.map((invoice, index) => (
          <div
            key={invoice.id}
            className="mx-auto mb-6 max-w-[210mm] overflow-hidden bg-white shadow-lg last:mb-0"
            data-testid={`master-invoice-preview-${invoice.id}`}
          >
            <iframe
              ref={(element) => {
                frameRefs.current[invoice.id] = element;
              }}
              srcDoc={invoice.html}
              title={`Eira master invoice ${invoice.id}`}
              className="h-[297mm] w-full border-0"
              data-testid={`master-invoice-frame-${invoice.id}`}
            />
            {index < invoiceMarkup.length - 1 && <div className="h-6 bg-[#F4F1EA]" />}
          </div>
        ))}
      </div>
    </div>
  );
}